import { useState } from 'react'
import { X } from 'lucide-react'

import { useWindowSize } from '@modules/hooks/useWindowSize'
import { cn } from '@modules/lib/lib'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@modules/shadcn/ui/Select/Select'

import type { ColorOption } from '@ai-color-modules/simulation/ColorPalette/colorOptions'
import { ColorPalette } from '@ai-color-modules/simulation/ColorPalette/ColorPalette'
import type {
  MaterialOption,
  MaterialTargetPart,
} from '@ai-color-modules/simulation/MaterialPalette/materialOptions'
import { MaterialPalette } from '@ai-color-modules/simulation/MaterialPalette/MaterialPalette'

import { ALL_COLORS_SENTINEL, type AreaColorPreset } from '../utils/presetSelection'

type DialogTab = 'color' | 'material'

interface ColorSelectDialogProps {
  selectedColor?: string
  onClose: () => void
  onColorSelect: (colorValue: string) => void
  className?: string
  /** API から取得した色一覧（必須） */
  colors: ColorOption[]
  /** API から取得した素材一覧（屋根・外壁ベースエリアでのみ使用） */
  materials?: MaterialOption[]
  /** 選択中の素材ID（未選択は undefined） */
  selectedMaterialId?: bigint
  /** 素材選択ハンドラ */
  onMaterialSelect?: (materialId: bigint) => void
  /** 素材タブ表示の対象部位. 未指定なら素材タブを出さない. */
  materialTargetPart?: MaterialTargetPart
  /** ダイアログ初期表示時の active タブ. 親側で areaSelectionMode から決める. */
  initialActiveTab?: DialogTab
  /** プリセット選択 Select を表示するか（企業のみ true、施主は false）. */
  showPresetSelector?: boolean
  /** 選択肢に出すプリセット一覧（色は display_order 順で解決済み）. */
  presets?: AreaColorPreset[]
  /** 現在選択中のプリセット ID または 'all'（すべての色）. */
  selectedPresetId?: string | typeof ALL_COLORS_SENTINEL
  /** プリセット切替コールバック. */
  onPresetChange?: (presetId: string | typeof ALL_COLORS_SENTINEL) => void
}

/**
 * 色指定 / 素材指定パネル
 * SP/TB: 下から出るハーフモーダル（固定位置）
 * LG+: モードレスな右側パネル
 *
 * 素材指定タブは `materialTargetPart` (`wall`|`roof`) が指定された時のみ表示する.
 * 屋根・外壁ベースエリア以外では色指定タブのみとなる.
 */
export const ColorSelectDialog: React.FC<ColorSelectDialogProps> = ({
  selectedColor,
  onClose,
  onColorSelect,
  className,
  colors,
  materials = [],
  selectedMaterialId,
  onMaterialSelect,
  materialTargetPart,
  initialActiveTab,
  showPresetSelector = false,
  presets = [],
  selectedPresetId = ALL_COLORS_SENTINEL,
  onPresetChange,
}) => {
  const { isPC } = useWindowSize()
  // プリセット選択 UI は「表示指定あり」かつ「プリセットが1件以上」のときだけ出す.
  const isPresetSelectorVisible = showPresetSelector && presets.length > 0
  const isMaterialTabAvailable = Boolean(materialTargetPart && onMaterialSelect)
  // activeTab は「編集中エリアのスコープに属する state」.
  // editingArea は truthy→truthy でも遷移する（パネルを開いたまま別エリアのボタンを押す）ため、
  // 初期化子だけでは initialActiveTab に追従できない。このスコープは親側の
  // `<ColorSelectDialog key={editingArea}>` で構造的に表現している（= エリアが変われば再マウント）.
  //
  // かつて置いていた「素材タブが利用不可になったら color へフォールバック」する useEffect は、
  // key 導入により到達不能（isMaterialTabAvailable は editingArea からのみ導出される）なため削除した.
  // 初期値の段階でのフォールバックは下の初期化子が担う.
  const [activeTab, setActiveTab] = useState<DialogTab>(
    initialActiveTab && (initialActiveTab !== 'material' || isMaterialTabAvailable)
      ? initialActiveTab
      : 'color',
  )

  const renderHeader = () => (
    <div className="flex items-center justify-between border-b border-[#E4E4E7] px-4 py-2">
      {isMaterialTabAvailable ? (
        <div className="inline-flex items-center gap-0 rounded-md bg-[#F4F4F5] p-1">
          <button
            aria-pressed={activeTab === 'color'}
            className={cn(
              'flex h-7 w-[120px] items-center justify-center rounded-sm text-sm font-medium transition-colors',
              activeTab === 'color' ? 'bg-white text-[#09090B]' : 'text-[#71717A]',
            )}
            onClick={() => setActiveTab('color')}
            type="button"
          >
            色指定
          </button>
          <button
            aria-pressed={activeTab === 'material'}
            className={cn(
              'flex h-7 w-[120px] items-center justify-center rounded-sm text-sm font-medium transition-colors',
              activeTab === 'material' ? 'bg-white text-[#09090B]' : 'text-[#71717A]',
            )}
            onClick={() => setActiveTab('material')}
            type="button"
          >
            素材指定
          </button>
        </div>
      ) : (
        <h2 className="flex-1 text-xs font-bold tracking-[-0.02em] text-[#09090B]">色指定</h2>
      )}
      <button
        aria-label={isMaterialTabAvailable ? '色・素材指定パネルを閉じる' : '色指定パネルを閉じる'}
        className="inline-flex h-4 w-4 items-center justify-center text-[#09090B]"
        onClick={onClose}
        type="button"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )

  const panelContent = (
    <div className="flex h-full min-h-0 flex-col bg-white shadow-lg lg:border-l lg:border-[#E4E4E7]">
      {renderHeader()}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-0 pt-4">
        {activeTab === 'color' || !isMaterialTabAvailable ? (
          <div className="flex h-full min-h-0 flex-col gap-3">
            {isPresetSelectorVisible ? (
              <Select
                value={selectedPresetId}
                onValueChange={value =>
                  onPresetChange?.(value as string | typeof ALL_COLORS_SENTINEL)
                }
              >
                <SelectTrigger aria-label="プリセットを選択" className="w-full">
                  <SelectValue placeholder="すべての色" />
                </SelectTrigger>
                {/*
                  SelectContent は body へポータルされ shadcn 既定で z-50。
                  SP ハーフモーダル（下記 aside は z-[70]・backdrop は z-[60]）の背後に
                  描画されると「プルダウンを押しても選択肢が見えない＝出ないように見える」
                  不具合になる（Radix ポータルは aside の stacking context 外）。
                  パネル(70)・backdrop(60) より前面へ出すため z-[80] を上書きする。
                */}
                <SelectContent className="z-[80]">
                  <SelectItem value={ALL_COLORS_SENTINEL}>すべての色</SelectItem>
                  {presets.map(preset => (
                    <SelectItem key={preset.id} value={preset.id}>
                      {preset.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
            <div className="min-h-0 flex-1">
              {/*
                key={selectedPresetId} は必須（見た目上の意味は無い）.
                ColorPalette 内の色フィルタ状態（useColorFilter のメーカー / トーン / 検索）は
                「選択中プリセットのスコープに属する state」であり、プリセットを跨いで残留させると
                「プリセットを切り替えても色候補が切り替わらない（0色が見つかりました）」になる。
                key で remount させることでスコープを構造的に表現しており、将来 useColorFilter に
                state が増えても自動的にこの不変条件が守られる.
              */}
              <ColorPalette
                colors={colors}
                key={selectedPresetId}
                onColorSelect={onColorSelect}
                selectedColor={selectedColor}
              />
            </div>
          </div>
        ) : (
          <MaterialPalette
            materials={materials}
            onMaterialSelect={id => onMaterialSelect?.(id)}
            selectedMaterialId={selectedMaterialId}
            targetPart={materialTargetPart!}
          />
        )}
      </div>
    </div>
  )

  if (!isPC) {
    return (
      <>
        <div
          aria-hidden="true"
          className="fixed inset-0 z-[60] bg-black/40"
          onClick={e => {
            e.stopPropagation()
            if (e.target === e.currentTarget) {
              onClose()
            }
          }}
          onPointerDown={e => {
            e.stopPropagation()
            if (e.target === e.currentTarget) {
              onClose()
            }
          }}
          onTouchMove={e => {
            e.stopPropagation()
          }}
          onTouchStart={e => {
            e.stopPropagation()
            if (e.target === e.currentTarget) {
              onClose()
            }
          }}
        />
        <aside
          className="fixed inset-x-0 bottom-0 z-[70] flex max-h-[70vh] flex-col overflow-hidden rounded-t-2xl border-t border-[#E4E4E7] bg-[#F4F4F5]"
          role="dialog"
        >
          <div className="flex justify-center py-2">
            <div className="h-1 w-10 rounded-full bg-[#D4D4D8]" />
          </div>
          {panelContent}
        </aside>
      </>
    )
  }

  return (
    <aside
      className={cn(
        'overflow-hidden rounded-md border border-[#E4E4E7] bg-[#F4F4F5]',
        'lg:h-full lg:min-h-[638px] lg:w-[344px] lg:rounded-r-none lg:border-r-0',
        className,
      )}
    >
      {panelContent}
    </aside>
  )
}
